import { Link } from "react-router-dom";
import { formatDate } from "../../../helper/dateFormatter";
import { Badge } from "../../../components/ui/Badge";
import { SummaryMedicationCard } from "./SummaryMedicationCard";
import { SummaryConditionCard } from "./SummaryConditionCard";

interface AppointmentRow {
  appointmentId: string;
  categoryName?: string;
  appointmentMode?: string;
  wfStateName?: string;
  coachName?: string;
  time: string | Date;
}

interface CareRecordBandProps {
  patientId: string;
  attendedCount: number;
  missedCount: number;
  appointments: AppointmentRow[];
}

const APPOINTMENT_ROW_LIMIT = 4;

// "Care record" band (design_handoff_patient_summary, option 2a):
// appointments, medications, conditions. Renders its own appointments row
// markup rather than reusing CompactAppointmentsCard, since that component
// is also used by PatientSummaryPanels.tsx (appointment charting sidebar)
// and isn't part of this redesign. Vaccinations was removed from this band -
// it still surfaces in NeedsAttentionBand (overdue/missing only).
export const CareRecordBand = ({
  patientId,
  attendedCount,
  missedCount,
  appointments,
}: CareRecordBandProps) => {
  const recentAppointments = [...appointments]
    .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
    .slice(0, APPOINTMENT_ROW_LIMIT);

  return (
    <div>
      <h2 className="ps-band-title">Care record</h2>
      <div className="ps-care-record-grid">
        <div className="ps-card ps-care-record-grid__appointments">
          <div className="ps-header-row">
            <span className="ps-section-title">Appointments</span>
            <Link className="ps-view-all" to={`/patients/${patientId}/appointments`}>
              View all
            </Link>
          </div>
          <div className="ps-appointments-summary">
            1:1 (last 30 days): {attendedCount} attended · {missedCount} missed
          </div>
          {recentAppointments.length === 0 ? (
            <span className="ps-empty">No appointments recorded</span>
          ) : (
            <div className="ps-record-list">
              {recentAppointments.map((appointment) => (
                <div className="ps-record-row" key={appointment.appointmentId}>
                  <span className="ps-record-row__date">{formatDate(appointment.time)}</span>
                  <span className="ps-record-row__desc">
                    {appointment.categoryName}
                    {appointment.appointmentMode ? `, ${appointment.appointmentMode}` : ""}
                    {appointment.coachName && (
                      <span className="ps-record-row__meta"> · {appointment.coachName}</span>
                    )}
                  </span>
                  {appointment.wfStateName && (
                    <Badge tone="info">{appointment.wfStateName}</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ps-card">
          <div className="ps-header-row">
            <span className="ps-section-title">Medications</span>
            <Link className="ps-view-all" to={`/patients/${patientId}/history`}>
              View all
            </Link>
          </div>
          <SummaryMedicationCard patientId={patientId} variant="care-record" />
        </div>

        <div className="ps-card">
          <div className="ps-header-row">
            <span className="ps-section-title">Conditions</span>
            <Link className="ps-view-all" to={`/patients/${patientId}/history`}>
              View all
            </Link>
          </div>
          <SummaryConditionCard patientId={patientId} variant="care-record" />
        </div>
      </div>
    </div>
  );
};
